import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const navLinks = [
  { label: 'Services', href: '/services/interior-painting' },
  { label: 'About', href: '/about' },
  { label: 'Areas', href: '/locations/elk-grove-village' },
  { label: 'Contact', href: '/#quote' },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  const handleAnchorClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    if (href.startsWith('/#')) {
      e.preventDefault();
      const id = href.replace('/#', '');
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 bg-white transition-all duration-500 ${
          scrolled ? 'border-b border-gray-200' : 'border-b border-gray-100'
        }`}
        style={{ height: scrolled ? '70px' : '84px' }}
      >
        <div className="max-w-[1400px] mx-auto px-6 h-full flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img
              src="https://storage.readdy-site.link/project_files/43399b4d-8768-47cb-9d95-8b96544cbfdf/4188435a-902a-4aaa-b9f5-c2ecb57cd5b3_prev_logo-300x67.png?v=0729ceb822395e8d71573e396bb5d448"
              alt="Hometown Painters"
              className="h-10 w-auto object-contain"
              style={{ maxWidth: '170px' }}
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={(e) => handleAnchorClick(e, link.href)}
                className="text-gray-600 hover:text-gray-900 font-sans text-sm tracking-wide uppercase transition-colors duration-200 cursor-pointer"
              >
                {link.label}
              </a>
            ))}
          </nav>

          {/* Desktop CTAs */}
          <div className="hidden md:flex items-center gap-4">
            <a
              href="tel:8478701600"
              className="flex items-center gap-2 text-gray-700 hover:text-gray-900 font-sans text-sm font-medium tracking-wide transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-phone-line" style={{ fontSize: '14px', color: '#b8963e' }} />
              </div>
              (847) 870-1600
            </a>
            <a
              href="#quote"
              onClick={(e) => handleAnchorClick(e, '/#quote')}
              className="bg-gray-900 text-white font-sans font-medium text-sm px-5 py-2.5 rounded-full hover:bg-gray-700 transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              Get a Quote
            </a>
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden w-10 h-10 flex items-center justify-center text-gray-700 cursor-pointer"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-3-line'} text-xl`} />
          </button>
        </div>
      </header>

      {/* Mobile sticky CTA bar */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 flex">
        <a
          href="tel:8478701600"
          className="flex-1 flex items-center justify-center gap-2 py-4 text-gray-800 font-sans text-sm font-medium border-r border-gray-200 cursor-pointer whitespace-nowrap"
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-phone-fill" style={{ fontSize: '16px', color: '#b8963e' }} />
          </div>
          Call Now
        </a>
        <a
          href="#quote"
          className="flex-1 flex items-center justify-center gap-2 py-4 bg-gray-900 text-white font-sans text-sm font-medium cursor-pointer whitespace-nowrap"
        >
          Get a Quote
        </a>
      </div>

      {/* Mobile Nav Overlay */}
      {menuOpen && (
        <div className="md:hidden fixed inset-0 z-40 bg-white flex flex-col items-center justify-center gap-8">
          {navLinks.map((link) => (
            <a
              key={link.label}
              href={link.href}
              onClick={(e) => handleAnchorClick(e, link.href)}
              className="font-serif text-3xl text-gray-800 hover:text-gray-500 transition-colors duration-200 cursor-pointer"
            >
              {link.label}
            </a>
          ))}
          <a
            href="tel:8478701600"
            className="flex items-center gap-2 font-sans text-lg font-medium text-gray-700 mt-4 cursor-pointer"
          >
            <div className="w-6 h-6 flex items-center justify-center">
              <i className="ri-phone-fill" style={{ fontSize: '20px', color: '#b8963e' }} />
            </div>
            (847) 870-1600
          </a>
        </div>
      )}
    </>
  );
}
